import { sample } from 'effector';
import { isEqual } from 'lodash';
import { $token, signout } from '@features/common';
import { fxGetTicketList, pageMounted } from '../page/page.model';
import {
  $pendingFilters,
  applyFilters,
  defaultFilters,
  filtersReadyToSubmit,
  filtersForm,
  fxSetLocaleStorage,
  setFilters,
} from './filters.model';
import { normalizeStoredFilters } from './normalize';

const storageKey = 'savedFiltersTicket';
const timeOfInactivity = 7 * 24 * 60 * 60 * 1000;

const getStoredFilters = () => {
  const value = localStorage.getItem(storageKey);

  if (!value) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(value);

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {};
  } catch {
    return {};
  }
};

const excludeFilters = (token: string) =>
  Object.fromEntries(
    Object.entries(getStoredFilters()).filter(([currentToken]) => currentToken !== token)
  );

const isStoredFilterActive = (token: string) => {
  const storedFilter = getStoredFilters()[token];

  return Boolean(storedFilter && Date.now() - storedFilter.timestamp <= timeOfInactivity);
};

$pendingFilters
  .on(applyFilters, (_, filters) => filters)
  .reset([filtersReadyToSubmit, signout]);

/** Сначала обновляем значения формы */
sample({
  clock: applyFilters,
  target: filtersForm.updateValues,
});

/** Ожидание новых значений, чтобы не отправить предыдущие */
sample({
  clock: filtersForm.values,
  source: $pendingFilters,
  filter: (pendingFilters, values) =>
    Boolean(pendingFilters) && isEqual(pendingFilters, values),
  fn: (_, values) => values,
  target: filtersReadyToSubmit,
});

/** После обновления значений применяются фильтры */
sample({
  clock: filtersReadyToSubmit,
  fn: () => undefined,
  target: filtersForm.submit,
});

/** Сброс прогоняем по той же схеме, что и сохраненные фильтры */
sample({
  clock: filtersForm.reset,
  fn: () => defaultFilters,
  target: applyFilters,
});

/** При выходе очищаем форму без отправки запроса */
sample({
  clock: signout,
  fn: () => defaultFilters,
  target: filtersForm.initValues,
});

/** Сброс зависимых полей при изменении payment */
sample({
  clock: filtersForm.setValue,
  filter: ({ path }) => path === 'payment',
  fn: () => ({
    path: 'invoiceStatus',
    value: null,
  }),
  target: filtersForm.setValue,
});

/** Сброс зависимых полей при изменении objects */
sample({
  clock: filtersForm.setValue,
  filter: ({ path }) => path === 'objects',
  fn: () => ({
    houses: [],
    apartments: [],
    property: [],
  }),
  target: filtersForm.updateValues,
});

/** Сброс зависимых полей при изменении houses */
sample({
  clock: filtersForm.setValue,
  filter: ({ path }) => path === 'houses',
  fn: () => ({
    apartments: [],
    property: [],
  }),
  target: filtersForm.updateValues,
});

/** Сохранение фильтров */
sample({
  clock: fxGetTicketList,
  source: { filters: filtersForm.initialValues, token: $token },
  filter: ({ token, filters }) => Boolean(token) && !isEqual(filters, defaultFilters),
  fn: ({ filters, token }) => ({
    key: storageKey,
    data: JSON.stringify({
      ...getStoredFilters(),
      [token as string]: { filters, timestamp: Date.now() },
    }),
  }),
  target: fxSetLocaleStorage,
});

/** Подстановка фильтров из сохраненных */
sample({
  clock: pageMounted,
  source: $token,
  filter: (token) => Boolean(token) && isStoredFilterActive(token as string),
  fn: (token) => normalizeStoredFilters(getStoredFilters()[token as string].filters),
  target: applyFilters,
});

/** Если фильтры сброшены, то очищаются из списка сохраненных */
sample({
  clock: fxGetTicketList,
  source: { filters: filtersForm.initialValues, token: $token },
  filter: ({ token, filters }) => Boolean(token) && isEqual(filters, defaultFilters),
  fn: ({ token }) => ({
    key: storageKey,
    data: JSON.stringify(excludeFilters(token as string)),
  }),
  target: fxSetLocaleStorage,
});

/** Применение фильтра, выбранного в тулбаре таблицы */
sample({
  clock: setFilters,
  fn: normalizeStoredFilters,
  target: applyFilters,
});

/** Если не истек период неактивности, то обновляется timestamp */
sample({
  clock: pageMounted,
  source: $token,
  filter: (token) => Boolean(token) && isStoredFilterActive(token as string),
  fn: (token) => {
    const storedFilters = getStoredFilters();
    const currentFilter = storedFilters[token as string];

    return {
      key: storageKey,
      data: JSON.stringify({
        ...storedFilters,
        [token as string]: { ...currentFilter, timestamp: Date.now() },
      }),
    };
  },
  target: fxSetLocaleStorage,
});

/** Если превышен период неактивности, то очищаются сохраненные фильтры */
sample({
  clock: pageMounted,
  source: $token,
  filter: (token) => {
    if (!token) {
      return false;
    }

    const storedFilter = getStoredFilters()[token];

    return Boolean(
      storedFilter && Date.now() - storedFilter.timestamp > timeOfInactivity
    );
  },
  fn: (token) => ({
    key: storageKey,
    data: JSON.stringify(excludeFilters(token as string)),
  }),
  target: fxSetLocaleStorage,
});
